import { useFragment, useQuery } from '@apollo/client';
import { Box } from '@mui/material';
import { ConversationParticipantEntity } from '@provision/conversation-types';
import { portalPaths, UrlParams } from '@provision/portal-routes';
import { usePrevious } from '@uidotdev/usehooks';
import { useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { MetaTags } from 'src/components/MetaTags/meta-tags';
import { gql } from 'src/gql';
import { useCustomViews } from 'src/graphql/entities/custom-view';
import { EntityNames } from 'src/graphql/types';
import { ClientWindow } from 'src/pages/Clients/client';
import { ClientSidePanel } from 'src/pages/Clients/SidePanel/side-panel';
import { EntityWindowWrapper } from 'src/pages/Conversations/shared-layout';
import { useGlobalStore } from 'src/store';
import { Modals, useModalMethods } from 'src/store/modals';
import { TabType, useGlobalTabs, useNotifications } from 'src/utils/hooks';
import { useNavigateRoute, useRouteParams } from 'src/utils/routing';
import { useScreenDimensions } from 'src/utils/system';

const ClientExpandedQuery = gql(`
  query ClientExpandedQueries($params: QueryParamsWithSearch) @entity(name: "Client") {
    getClient(queryParams: $params) {
      records {
        id
        ...ClientExpanded_Client
      }
    }
  }
`);

const ClientExpandedFragment = gql(`
  fragment ClientExpanded_Client on Client {
    id
    clientId
    partnerId
    firstName
    lastName
    fullName @client
    primaryEmail {
      id
      value
    }
    ...ClientWindow_Client
    ...ClientSidePanel_Client
  }
`);

export const ClientExpanded = () => {
  const { showErrorAlert } = useNotifications();
  const { isMobile } = useScreenDimensions();
  const location = useLocation();
  const { navigate } = useNavigateRoute();
  const { recordId: clientId } = useRouteParams(UrlParams.recordId);
  const { openInTab, findTab, closeTab } = useGlobalTabs();

  const result = useQuery(ClientExpandedQuery, {
    variables: {
      params: {
        filters: [
          {
            attribute: 'clientId',
            operator: '=',
            value: clientId,
          },
        ],
        includeDeleted: true,
      },
    },
    skip: !clientId,
  });

  const { data: client, complete } = useFragment({
    fragment: ClientExpandedFragment,
    fragmentName: 'ClientExpanded_Client',
    from: {
      __typename: 'Client',
      id: clientId,
    },
  });

  const currentPartnerId = useGlobalStore.use.selectedPartnerId();

  const { setModalProps: setAddConversationModalProps } = useModalMethods(
    Modals.AddConversationModal,
  );

  useEffect(() => {
    let deleteAddConversationModalProps: (() => void) | undefined;
    if (currentPartnerId && complete) {
      deleteAddConversationModalProps = setAddConversationModalProps({
        requester: {
          id: client.clientId,
          label: client.fullName,
          participantEntity: ConversationParticipantEntity.Client,
          email: client.primaryEmail?.value ?? undefined,
          data: client,
        },
      });
    }
    return () => {
      deleteAddConversationModalProps?.();
    };
  }, [currentPartnerId, setAddConversationModalProps, client, complete]);

  const customViewId = (location.state as { customViewId?: string })?.customViewId;

  const { customViews } = useCustomViews({
    entity: EntityNames.Client,
  });

  const customView = useMemo(
    () => (customViewId ? customViews.find((i) => i.customViewId === customViewId) : null),
    [customViews, customViewId],
  );

  const prevClient = usePrevious(complete ? client : undefined);

  useEffect(() => {
    // handle client deletion (the record disappears from the cache, so the
    // fragment flips back to incomplete)
    if (!complete) return;
    if (prevClient) {
      const tab = findTab(prevClient.clientId, TabType.Client);
      if (tab) {
        closeTab(tab);
      }
      navigate(
        portalPaths.entity.all({
          entityName: 'clients',
          customViewId: customView?.customViewId,
        }),
      );
    }
  }, [complete, closeTab, customView?.customViewId, findTab, navigate, prevClient]);

  // open expanded site in tab.
  useEffect(() => {
    // gate on the query (not on `complete`): when the record does not exist the
    // fragment never completes, and we still must redirect once the query settles
    if (result.loading) return;
    if (complete) {
      openInTab(
        {
          type: TabType.Client,
          entity: {
            id: client.clientId,
            clientId: client.clientId,
            partnerId: client.partnerId,
          },
        },
        { customViewId: customView?.customViewId },
      );
    } else {
      showErrorAlert(`Client "${clientId}" not found. You've been redirected to All Clients.`);
      navigate(
        portalPaths.entity.all({
          entityName: 'clients',
          customViewId: customView?.customViewId,
        }),
      );
    }
    // this hooks needs to run only once when the client is loaded
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result.loading]);

  const onCloseClient = useCallback(() => {
    if (customView?.customViewId) {
      navigate(
        portalPaths.entity.selected(
          customView.entityName,
          customView.customViewId,
          !isMobile ? clientId : undefined,
        ),
      );
    } else {
      navigate(
        portalPaths.entity.all({
          entityName: 'client',
          id: isMobile ? clientId : '',
        }),
      );
    }
  }, [customView?.customViewId, customView?.entityName, isMobile, navigate, clientId]);

  return complete ? (
    <MetaTags title={client.fullName}>
      <Box height="100%" position="relative">
        <EntityWindowWrapper>
          {isMobile ? (
            <ClientSidePanel clientId={clientId} />
          ) : (
            <ClientWindow isSidePanelExpanded clientId={clientId} onCloseClient={onCloseClient} />
          )}
        </EntityWindowWrapper>
      </Box>
    </MetaTags>
  ) : null;
};
